import type { Field } from '@/api/types/crm/crm.types'
import {
  UTM_LEAD_FIELD_TOOLTIPS,
  type UtmLeadFieldUniqueName,
} from '@pages/Admin/CRM/utils/utmLeadFields'

export type DealUtm = {
  source: string | null
  medium: string | null
  campaign: string | null
  content: string | null
  term: string | null
}

export type DealUtmKey = keyof DealUtm

export const EMPTY_DEAL_UTM: DealUtm = {
  source: null,
  medium: null,
  campaign: null,
  content: null,
  term: null,
}

export const DEAL_UTM_KEYS: DealUtmKey[] = [
  'source',
  'medium',
  'campaign',
  'content',
  'term',
]

const DEAL_UTM_TO_LEGACY: Record<DealUtmKey, UtmLeadFieldUniqueName> = {
  source: 'UTM_SOURCE',
  medium: 'UTM_MEDIUM',
  campaign: 'UTM_CAMPAIGN',
  content: 'UTM_CONTENT',
  term: 'UTM_TERM',
}

const DEAL_UTM_LABELS: Record<DealUtmKey, string> = {
  source: 'Source',
  medium: 'Medium',
  campaign: 'Campaign',
  content: 'Content',
  term: 'Term',
}

export const normalizeDealUtmValue = (
  raw: string | null | undefined,
): string | null => {
  if (raw == null) return null
  const trimmed = String(raw).trim()
  return trimmed === '' ? null : trimmed.slice(0, 255)
}

export const normalizeDealUtm = (
  raw: Partial<DealUtm> | Record<string, unknown> | null | undefined,
): DealUtm => {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_DEAL_UTM }

  const read = (...keys: string[]) => {
    for (const key of keys) {
      if (key in raw) {
        return normalizeDealUtmValue(
          (raw as Record<string, unknown>)[key] as string | null | undefined,
        )
      }
    }
    return null
  }

  return {
    source: read('source', 'utm_source'),
    medium: read('medium', 'utm_medium'),
    campaign: read('campaign', 'utm_campaign'),
    content: read('content', 'utm_content'),
    term: read('term', 'utm_term'),
  }
}

/** Тело PATCH update-card-funnel: бэк ждёт ключи utm_*. */
export const dealUtmToApiPayload = (
  utm: DealUtm,
): {
  utm_source: string | null
  utm_medium: string | null
  utm_campaign: string | null
  utm_content: string | null
  utm_term: string | null
} => ({
  utm_source: utm.source,
  utm_medium: utm.medium,
  utm_campaign: utm.campaign,
  utm_content: utm.content,
  utm_term: utm.term,
})

export const dealUtmEquals = (a: DealUtm, b: DealUtm): boolean =>
  DEAL_UTM_KEYS.every((key) => a[key] === b[key])

export const getDealUtmFieldTooltip = (key: DealUtmKey): string =>
  UTM_LEAD_FIELD_TOOLTIPS[DEAL_UTM_TO_LEGACY[key]]

/** Синтетические Field для вкладки UTM в режиме сделок. */
export const buildDealUtmLeadFields = (): Field[] =>
  DEAL_UTM_KEYS.map((key, index) => ({
    id: -9100 - index,
    unique_name: `__DEAL_UTM_${key.toUpperCase()}__`,
    name: DEAL_UTM_LABELS[key],
    type: 'textfield',
    value_options: null,
    is_active: true,
    is_default: true,
    is_required: false,
    sorting: index + 1,
    settings: {},
  }))

export const dealUtmKeyFromSyntheticUniqueName = (
  uniqueName: string,
): DealUtmKey | null => {
  const match = uniqueName.match(/^__DEAL_UTM_([A-Z]+)__$/)
  if (!match) return null
  const key = match[1].toLowerCase() as DealUtmKey
  return DEAL_UTM_KEYS.includes(key) ? key : null
}
